/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Timestamp handling now comes from the kit; what remains here is the epoch-millis shape this
 * app's sorting and comparison code wants.
 *
 * The two `normalizeDbTimestamp` implementations had each solved half the problem. This one padded
 * PostgreSQL's truncated offset (`-05` to `-05:00`) but left a zone-less timestamp as local time,
 * so `2026-01-15 10:30:00` read an hour differently for a viewer in another zone. The kit's pinned
 * zone-less values to UTC but could not read the offset at all. The kit's version now does both,
 * with tests for each case.
 *
 * `formatRelativeTime` is deliberately not re-exported. The version that lived here returned
 * hardcoded English ("3h ago") in an app that ships 13 locales; the kit's goes through
 * `Intl.RelativeTimeFormat`, so callers should reach for that directly and pass the active
 * language. See `features/home/components/ContinueSection.tsx`.
 */
import { parseDate } from '@vaas/react-ui-kit';

export { normalizeDbTimestamp } from '@vaas/react-ui-kit';

/**
 * Parses a DB timestamp to epoch millis, or `null` when absent or unparseable.
 *
 * Millis rather than the kit's `Date` because every caller is ordering or comparing rather than
 * displaying, and `??`-ing a number against a default is far tidier than doing it with a Date.
 */
export function parseDbTimestamp(raw?: string): number | null {
  return parseDate(raw)?.getTime() ?? null;
}

/**
 * A DB timestamp as the technician reads it, or `null` when absent or unparseable.
 *
 * Rendered in UTC because these stamps are dates rather than instants: read locally, a date-only
 * stamp lands a day early for anyone west of Greenwich.
 */
export function shortDbDate(raw: string | undefined, locale: string): string | null {
  const at = parseDbTimestamp(raw);
  if (at === null) return null;
  return new Date(at).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
